import React from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  Image,
  VStack,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  BIZFLEX_README_DOCS_BASE,
} from "../../components/common/constants";
import { useBizflexAppUrls } from "../../utils/useBizflexAppUrls";

const ApiUseCases = () => {
  const { registerUrl } = useBizflexAppUrls();
  const useCases = [
    {
      icon: "/assets/fintech.svg",
      title: "Fintechs and Digital Banks",
      description:
        "Launch wallet systems, manage KYC, and handle transfers seamlessly. Build the next generation of digital banking with our complete infrastructure.",
      features: [
        "Complete wallet infrastructure",
        "KYC verification integration",
        "Multi-currency support",
        "Real-time transaction processing",
      ],
    },
    {
      icon: "/assets/marketplace.svg",
      title: "Marketplaces and Platforms",
      description:
        "Automate vendor payouts and reconciliation using virtual accounts. Scale your marketplace with seamless payment infrastructure.",
      features: [
        "Automated vendor settlements",
        "Split payment capabilities",
        "Virtual account per vendor",
        "Instant reconciliation",
      ],
    },
    {
      icon: "/assets/saas.svg",
      title: "SaaS and Subscription Businesses",
      description:
        "Enable recurring payments and flexible billing options. Automate your subscription management with our powerful APIs.",
      features: [
        "Recurring payment automation",
        "Flexible billing cycles",
        "Failed payment retry logic",
        "Dunning management",
      ],
    },
    {
      icon: "/assets/global.svg",
      title: "Global Businesses",
      description:
        "Accept and settle USD payments from over 100 countries (coming soon). Expand your reach with international payment capabilities.",
      features: [
        "USD virtual accounts",
        "International payouts",
        "Competitive FX rates",
        "Multi-currency wallets",
      ],
    },
  ];

  // Checkmark Icon Component
  const CheckmarkIcon = () => (
    <Box
      w="16px"
      h="16px"
      borderRadius="50%"
      bg="#10B981"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.33329 2.5L3.74996 7.08333L1.66663 5"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );

  return (
    <Box
      bg="#F5F9FC"
      w="100vw"
      position="relative"
      left="50%"
      right="50%"
      ml="-50vw"
    >
      <Box
        w="100%"
        maxW="1326px"
        mx="auto"
        py={{ base: "60px", md: "100px" }}
        px={{ base: "20px", md: "80px", lg: "120px" }}
      >
        <VStack spacing="60px" align="center" w="100%">
        {/* Top Section */}
        <VStack spacing="24px" align="center" w="100%" maxW="800px">
          {/* Badge */}
          <Badge
            display="flex"
            alignItems="center"
            gap="8px"
            w="fit-content"
            bg="#F8FAFC"
            color="#2463EB"
            px="16px"
            py="8px"
            border="1px solid #93C5FD"
            borderRadius="20px"
            fontSize="12px"
            fontWeight={400}
          >
            <Image src="/assets/bulb.svg" alt="use-cases" w="16px" h="16px" />
            Use Cases
          </Badge>

          {/* Main Heading - Centered */}
          <Text
            fontSize={{ base: "32px", md: "48px" }}
            fontWeight={700}
            lineHeight={{ base: "38.4px", md: "57.6px" }}
            color="#0F172B"
            textAlign="center"
          >
            What You Can Build
          </Text>

          {/* Description - Centered */}
          <Text
            fontSize={{ base: "14px", md: "18px" }}
            lineHeight={{ base: "21px", md: "27px" }}
            color="#64748B"
            textAlign="center"
            maxW="700px"
          >
            Unlock new possibilities for your business or platform with BizFlex's
            comprehensive API suite
          </Text>
        </VStack>

        {/* Middle Section - Feature Cards in 2x2 Grid */}
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={{ base: "24px", md: "32px" }}
          w="100%"
          maxW="1200px"
        >
          {useCases.map((useCase, index) => (
            <Box
              key={index}
              bg="#ffffff"
              borderRadius="16px"
              p="32px"
              border="1px solid #E2E8F0"
              boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)"
              _hover={{
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                transition: "all 0.3s ease",
              }}
              h="100%"
            >
              <VStack align="flex-start" spacing="20px" h="100%">
                {/* Icon */}
                <Box>
                  <Image
                    src={useCase.icon}
                    alt={useCase.title}
                    w="48px"
                    h="48px"
                  />
                </Box>

                {/* Title */}
                <Text
                  fontSize={{ base: "18px", md: "20px" }}
                  fontWeight={700}
                  color="#0F172B"
                >
                  {useCase.title}
                </Text>

                {/* Description */}
                <Text
                  fontSize={{ base: "14px", md: "16px" }}
                  color="#64748B"
                  lineHeight="24px"
                >
                  {useCase.description}
                </Text>

                {/* Features List */}
                <VStack align="flex-start" spacing="18px" w="100%" flex={1}>
                  {useCase.features.map((feature, featureIndex) => (
                    <HStack
                      key={featureIndex}
                      align="flex-start"
                      spacing="12px"
                      w="100%"
                    >
                      <CheckmarkIcon />
                      <Text
                        fontSize={{ base: "13px", md: "14px" }}
                        color="#475569"
                        lineHeight="20px"
                      >
                        {feature}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                {/* Learn More Link */}
                <Box
                  as="a"
                  href={BIZFLEX_README_DOCS_BASE}
                  target="_blank"
                  rel="noopener noreferrer"
                  mt="auto"
                >
                  <HStack
                    spacing="4px"
                    color="#2463EB"
                    fontWeight={500}
                    fontSize="14px"
                    _hover={{ color: "#1a4fc7" }}
                  >
                    <Text>Learn more</Text>
                    <Image
                      src="/assets/arrow-right2.svg"
                      alt=""
                      w="16px"
                      h="16px"
                    />
                  </HStack>
                </Box>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>

        {/* Bottom Section - Centered CTA Button */}
        <Flex justifyContent="center" w="100%">
          <Button
            as="a"
            href={registerUrl}
            target="_blank"
            rel="noopener noreferrer"
            bg="#2463EB"
            borderRadius="12px"
            h="52px"
            w={{ base: "100%", md: "240px" }}
            color="#fff"
            fontWeight={500}
            fontSize="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="8px"
            _hover={{ bg: "#1a4fc7" }}
          >
            Get Started Now
            <Image
              src="/assets/rocket.svg"
              alt=""
              w="17px"
              h="17px"
            />
          </Button>
        </Flex>
        </VStack>
      </Box>
    </Box>
  );
};

export default ApiUseCases;

